import React from 'react';

const ENotice = ({ language = 'en' }) => {
  const content = {
    en: {
      title: 'Electronic Notification System',
      subtitle: 'Smart Digital Notice System',
      intro: 'Launch a dedicated smart digital notification system for buildings and communities, replacing traditional paper notices. Achieve efficient information release, visual reading, and precise communication. Integrated with lobby digital screens, ensuring residents receive important information instantly.',
      features: {
        title: 'Core Features',
        items: [
          'Multi-channel Delivery: Simultaneously send management alerts, event invitations, and service outage warnings',
          'Screen Carousel Display: Automatic content rotation with clear typography, optimized for lobby screens',
          'Read Receipt Tracking: Real-time statistics on backend system',
          'Role-based Access Control: Classify and deliver content separately for admin and manager'
        ]
      },
      advantages: {
        title: 'Advantages',
        items: [
          'Instant Delivery: Eliminates dust, loss, and delays of paper notices; updates instantly',
          'Full Reachability: 24/7 display in the lobby guarantees 100% coverage, no missed important updates',
          'Cost-saving: Reduces printing and labor costs, promoting environmental sustainability',
          'Digital Record: Accessible history of all notifications for easy management and future reference'
        ]
      },
      process: {
        title: 'Service Process',
        steps: 'Requirement Configuration → Design Integration → System Launch → Daily Use → Backend Maintenance'
      }
    },
    zh: {
      title: '電子通告系統',
      subtitle: '智能電子通告系統',
      intro: '打造大樓與社區專属的智能電子通告系統，取代傳統張貼紙公告，實現訊息發布高效化、閱讀可視化、通知精准化。結合大堂電子屏幕，確保住戶第一時間掌握重要資訊，提升溝通效率。',
      features: {
        title: '核心功能',
        items: [
          '多頻道發布：同時發送管理通知、活動邀請、停水停電警示等資訊',
          '螢幕輪播顯示：內容自動輪播，字體清晰，適配大堂電子屏幕',
          '後台統計：後台實時統計播放次數，確保訊息到達率',
          '分類權限管理：可依據經理、管理層進行分級推送'
        ]
      },
      advantages: {
        title: '系統優勢',
        items: [
          '即時傳達：消除紙張公告落塵、遺失問題，訊息秒更新',
          '提升觸達：24小時顯示於大堂，覆蓋率100%，不漏接重要通知',
          '節省成本：減少印刷張貼人事成本，綠色環保',
          '數位留痕：通告歷史記錄可查詢，便備管理與事後追溯'
        ]
      },
      process: {
        title: '服務流程',
        steps: '需求設定 → 畫面設計 → 系統上架 → 上線使用 → 後台維護'
      }
    }
  };

  const currentContent = content[language] || content.en;

  return (
    <section id="enotice-system" className="service__section pt-120 pb-120">
      <div className="container">
        <div className="project__head text-center mb-60">
          <span className="common__sub aos-init aos-animate" data-aos="fade-down" data-aos-duration="1000">
            {currentContent.title}
          </span>
          <h2 className="fw-500 aos-init aos-animate" data-aos="fade-up" data-aos-duration="1200" style={{ color: '#c9f31d' }}>
            {currentContent.subtitle}
          </h2>
        </div>

        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          {/* Hero Image */}
          <div style={{ position: 'relative', zIndex: 1, borderRadius: '12px', overflow: 'hidden', marginBottom: '2rem' }}>
            <img
              src="/assets/photos/electronic-notification.jpeg"
              alt="Electronic Notification System"
              style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '12px' }}
            />
          </div>
          {/* Intro */}
          <div 
            className="aos-init aos-animate"
            data-aos="fade-up"
            data-aos-duration="1000"
            style={{
              background: 'rgba(255, 255, 255, 0.03)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              marginBottom: '2rem',
              transition: 'all 0.3s ease'
            }}
          >
            <p className="pra fz-18" style={{
              lineHeight: '1.9',
              color: '#e0e0e0',
              textAlign: 'justify',
              margin: 0
            }}>
              {currentContent.intro}
            </p>
          </div>

          {/* Features */}
          <div 
            className="aos-init aos-animate"
            data-aos="fade-up"
            data-aos-duration="1000"
            data-aos-delay="200"
            style={{
              background: 'rgba(255, 255, 255, 0.03)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              marginBottom: '2rem',
              transition: 'all 0.3s ease'
            }}
          >
            <h3 className="fz-24 fw-600 mb-3" style={{ color: '#c9f31d' }}>
              {currentContent.features.title}
            </h3>
            <ul style={{ listStyle: 'none', padding: '0', margin: '0' }}>
              {currentContent.features.items.map((item, idx) => (
                <li key={idx} className="pra fz-16 mb-2" style={{ 
                  paddingLeft: '1.5rem',
                  position: 'relative',
                  lineHeight: '1.7',
                  color: '#e0e0e0'
                }}>
                  <span style={{ 
                    position: 'absolute',
                    left: '0',
                    color: '#c9f31d',
                    fontWeight: 'bold'
                  }}>•</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Advantages */}
          <div 
            className="aos-init aos-animate"
            data-aos="fade-up"
            data-aos-duration="1000"
            data-aos-delay="400"
            style={{
              background: 'rgba(255, 255, 255, 0.03)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              marginBottom: '2rem',
              transition: 'all 0.3s ease'
            }}
          >
            <h3 className="fz-24 fw-600 mb-3" style={{ color: '#c9f31d' }}>
              {currentContent.advantages.title}
            </h3>
            <ul style={{ listStyle: 'none', padding: '0', margin: '0' }}>
              {currentContent.advantages.items.map((item, idx) => (
                <li key={idx} className="pra fz-16 mb-2" style={{ 
                  paddingLeft: '1.5rem',
                  position: 'relative',
                  lineHeight: '1.7',
                  color: '#e0e0e0'
                }}>
                  <span style={{ 
                    position: 'absolute',
                    left: '0',
                    color: '#c9f31d',
                    fontWeight: 'bold'
                  }}>•</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Process */}
          <div 
            className="aos-init aos-animate"
            data-aos="fade-up"
            data-aos-duration="1000"
            data-aos-delay="600"
            style={{
              background: 'linear-gradient(135deg, rgba(48, 102, 255, 0.1) 0%, rgba(201, 243, 29, 0.1) 100%)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '1px solid rgba(201, 243, 29, 0.2)',
              transition: 'all 0.3s ease'
            }}
          >
            <h3 className="fz-24 fw-600 mb-3" style={{ color: '#c9f31d' }}>
              {currentContent.process.title}
            </h3>
            <p className="pra fz-18" style={{
              lineHeight: '1.9',
              color: '#c9f31d',
              textAlign: 'center',
              margin: 0,
              fontWeight: '500'
            }}>
              {currentContent.process.steps}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ENotice;
